// Iter 9.50+ R18 - Preference card final look + visibility redesign.
// New flows live at /design/preference-card?s=X and /design/final-look?s=X
// and /design/visibility?s=X and /design/media-library?s=X.
import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Camera, Check, ChevronDown, ChevronRight, ChevronUp, Clock,
  Edit, Eye, EyeOff, Folder, Globe, Heart, ImageIcon, Lock, MoreVertical,
  Plus, RefreshCw, Settings, Share2, Sparkles, Star, Tag, ToggleLeft, Trash2,
  Upload, User, Users, X, Calendar as CalendarIcon, Camera as CameraIcon, Link2
} from "lucide-react";

const FRAME = "max-w-[420px] mx-auto bg-white min-h-screen shadow-xl";

const Header = ({ title, back, right }) => (
  <header className="sticky top-0 z-20 bg-white border-b border-stone-200 px-4 h-14 flex items-center justify-between">
    {back ? (
      <Link to={back} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full">
        <ArrowLeft className="w-4 h-4" />
      </Link>
    ) : <div className="w-8 h-8" />}
    <h1 className="text-sm font-bold text-stone-900 uppercase tracking-wider">{title}</h1>
    <div className="w-8 h-8 flex items-center justify-center">{right || null}</div>
  </header>
);

const StateSwitcher = ({ flow, states, current }) => (
  <div className="bg-stone-100 border-t border-stone-200 px-3 py-2 flex flex-wrap gap-1 sticky bottom-0">
    <span className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mr-2 self-center">States</span>
    {states.map((s) => (
      <Link key={s.id} to={`/design/${flow}?s=${s.id}`}
        className={`text-[10px] px-2 py-1 rounded-full ${current === s.id ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600"}`}>
        {s.label}
      </Link>
    ))}
  </div>
);

// Visibility chip with icon
const VIS_OPTIONS = {
  public: { label: "Public", icon: Globe, color: "emerald" },
  host: { label: "Host only", icon: Users, color: "indigo" },
  private: { label: "Eyes only", icon: EyeOff, color: "stone" },
};
const VisBadge = ({ v }) => {
  const opt = VIS_OPTIONS[v] || VIS_OPTIONS.public;
  const Icon = opt.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-${opt.color}-100 text-${opt.color}-700 px-1.5 py-0.5 rounded-full`}>
      <Icon className="w-2.5 h-2.5" /> {opt.label}
    </span>
  );
};

// ============================================================================
// PREFERENCE CARD (eye-icon view) - 7 states
// ============================================================================
const PC_STATES = [
  { id: "no-service-done", label: "Pre-service (3 inspo top)" },
  { id: "before-prompt", label: "Before look prompt" },
  { id: "after-1-photo", label: "1 final look" },
  { id: "after-multiple", label: "3 final looks" },
  { id: "image-picker", label: "Owner picks top 3" },
  { id: "before-picker", label: "Owner picks before" },
  { id: "name-display", label: "Name display toggle" },
];

function PreferenceCardDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "no-service-done";
  return (
    <div className={FRAME}>
      <Header title="Preference Card" back="/design" right={<MoreVertical className="w-4 h-4 text-stone-500" />} />
      <div className="px-4 pt-3 pb-32 space-y-4 bg-stone-50">
        {s === "no-service-done" && <PCStatePreService />}
        {s === "before-prompt" && <PCStateBeforePrompt />}
        {s === "after-1-photo" && <PCStateAfter1Photo />}
        {s === "after-multiple" && <PCStateAfterMultiple />}
        {s === "image-picker" && <PCImagePicker />}
        {s === "before-picker" && <PCBeforePicker />}
        {s === "name-display" && <PCNameDisplay />}
      </div>
      <StateSwitcher flow="preference-card" states={PC_STATES} current={s} />
    </div>
  );
}

// Card hero - 3-image strip at top + meta
const CardHero = ({ topImages, status = "planning", category = "Makeup", title, date, name, ownerControl }) => {
  const cells = topImages.length > 0 ? topImages : [null, null, null];
  return (
    <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
      <div className="grid grid-cols-3 gap-px bg-stone-100" data-testid="pc-hero-top-images">
        {[0, 1, 2].map((i) => (
          <div key={i} className="aspect-square bg-rose-100 flex items-center justify-center relative overflow-hidden">
            {cells[i] ? (
              <img src={cells[i]} alt="" className="w-full h-full object-cover" />
            ) : (
              <Sparkles className="w-4 h-4 text-rose-300" />
            )}
            {cells[i] && i === 0 && cells[i].startsWith && cells[i].includes("FINAL") && (
              <span className="absolute top-1 left-1 text-[7px] uppercase tracking-wider bg-emerald-600 text-white px-1 py-0.5 rounded font-bold">After</span>
            )}
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{status}</span>
          <span className="text-[9px] uppercase tracking-wider border border-stone-300 text-stone-700 px-2 py-0.5 rounded-full">{category}</span>
        </div>
        <h2 className="text-lg font-bold text-stone-900">{title}</h2>
        <div className="flex items-center gap-3 text-xs text-stone-600">
          <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {date}</span>
        </div>
        <p className="text-[10px] text-stone-500">For <strong>{name}</strong></p>
        {ownerControl && (
          <Link to="/design/preference-card?s=image-picker" className="block mt-2 text-[10px] text-purple-700 font-semibold flex items-center gap-1" data-testid="pc-owner-pick-top">
            <Edit className="w-3 h-3" /> Owner: pick which 3 images show here
          </Link>
        )}
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, visibility = "host", children, action }) => (
  <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-2.5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-stone-500" />}
        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{title}</p>
      </div>
      <div className="flex items-center gap-2">
        <VisBadge v={visibility} />
        {action}
      </div>
    </div>
    {children}
  </div>
);

const PCStatePreService = () => (
  <div className="space-y-3" data-testid="state-pc-no-service-done">
    <CardHero
      topImages={[
        "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300",
        "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300",
        "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300",
      ]}
      title="Sister&apos;s wedding glam"
      date="Jun 15, 2026 - 10:30 AM"
      name="Aria"
      ownerControl
    />
    <p className="text-[10px] text-stone-500 px-1 -mt-2">Top 3 = inspo until the service is done. After, you can flip them to before/after photos.</p>
    <Section icon={Sparkles} title="Inspiration (3 of 12)" visibility="public" action={<button className="text-[10px] text-purple-700 font-semibold">View all</button>}>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300",
          "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300",
          "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300",
        ].map((u, i) => <div key={i} className="aspect-square rounded-md bg-cover" style={{backgroundImage:`url(${u})`}} />)}
      </div>
    </Section>
    {/* Empty "before look" - collapsed since text + image are empty */}
    <BeforeLookSection state="empty" />
    {/* Empty "final look" */}
    <FinalLookSection state="empty" />
    <Section icon={Tag} title="Preferences" visibility="host">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><p className="text-[9px] uppercase text-stone-400">Skin type</p><p className="text-stone-800 font-medium">Combination</p></div>
        <div><p className="text-[9px] uppercase text-stone-400">Undertone</p><p className="text-stone-800 font-medium">Warm</p></div>
        <div><p className="text-[9px] uppercase text-stone-400">Eye shape</p><p className="text-stone-800 font-medium">Almond</p></div>
        <div><p className="text-[9px] uppercase text-stone-400">Lash style</p><p className="text-stone-800 font-medium">Natural volume</p></div>
      </div>
    </Section>
    <Section icon={Tag} title="Products" visibility="public">
      <div className="grid grid-cols-3 gap-2">
        {["Fenty 240","Pillow Talk","Soft Glam"].map((p,i) =>
          <div key={i} className="text-[9px] text-stone-700 leading-tight"><div className="aspect-square bg-stone-200 rounded mb-1" /><p className="font-medium truncate">{p}</p></div>
        )}
      </div>
    </Section>
    <Link to="/design/visibility" className="block text-center text-xs text-purple-700 font-semibold py-2" data-testid="pc-cta-visibility">
      <Settings className="w-3 h-3 inline mr-1" /> Visibility settings
    </Link>
  </div>
);

const PCStateBeforePrompt = () => (
  <div className="space-y-3" data-testid="state-pc-before-prompt">
    <CardHero
      topImages={[
        "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300",
        "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300",
        "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300",
      ]}
      title="Sister&apos;s wedding glam"
      date="Jun 15, 2026 - 10:30 AM"
      name="Aria"
      ownerControl
    />
    <BeforeLookSection state="prompt" />
    <FinalLookSection state="empty" />
  </div>
);

const PCStateAfter1Photo = () => (
  <div className="space-y-3" data-testid="state-pc-after-1-photo">
    <CardHero
      topImages={[
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&FINAL",
        "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300",
        "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300",
      ]}
      status="completed"
      title="Sister&apos;s wedding glam"
      date="Jun 15, 2026 - DONE"
      name="Aria"
      ownerControl
    />
    <p className="text-[10px] text-stone-500 px-1 -mt-2">Service complete. Top-left flipped to the final look. Toggle any image into the top 3.</p>
    <BeforeLookSection state="filled-1" />
    <FinalLookSection state="single" />
  </div>
);

const PCStateAfterMultiple = () => (
  <div className="space-y-3" data-testid="state-pc-after-multiple">
    <CardHero
      topImages={[
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&FINAL",
        "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400&FINAL",
        "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=400&FINAL",
      ]}
      status="completed"
      title="Sister&apos;s wedding glam"
      date="Jun 15, 2026 - DONE"
      name="Aria"
      ownerControl
    />
    <BeforeLookSection state="filled-1" />
    <FinalLookSection state="multi" />
  </div>
);

// --- BEFORE LOOK section
const BeforeLookSection = ({ state }) => {
  if (state === "empty") {
    return (
      <div className="rounded-2xl bg-stone-50 border border-dashed border-stone-200 px-4 py-2.5 flex items-center justify-between" data-testid="before-look-empty-toggled-up">
        <div className="flex items-center gap-2 text-stone-500">
          <ChevronUp className="w-3 h-3" />
          <p className="text-[10px] uppercase tracking-wider font-bold">Before look (none added)</p>
        </div>
        <button className="text-[10px] text-purple-700 font-semibold">+ Add</button>
      </div>
    );
  }
  return (
    <Section icon={Camera} title="Before look" visibility={state === "prompt" ? "host" : "public"} action={<Link to="/design/preference-card?s=before-picker" className="text-[10px] text-purple-700 font-semibold">Change</Link>}>
      {state === "prompt" ? (
        <div className="space-y-2">
          <p className="text-[11px] text-stone-600">Drop a bare-face or starting-point pic so the after is more dramatic. Totally optional.</p>
          <button className="w-full py-3 rounded-xl border-2 border-dashed border-stone-300 text-stone-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-stone-500" data-testid="before-look-upload-cta">
            <Upload className="w-4 h-4" /> Upload a before pic
          </button>
          <textarea placeholder="A short caption (optional)" rows={2} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs resize-none" />
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="w-20 h-20 rounded-xl bg-stone-200 overflow-hidden flex-shrink-0">
            <img src="https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 text-xs">
            <p className="text-stone-700 leading-snug italic">&ldquo;Coming straight from the gym - no filter.&rdquo;</p>
            <p className="text-[10px] text-stone-400 mt-1">Uploaded by Aria - 9:42 AM</p>
          </div>
        </div>
      )}
    </Section>
  );
};

// --- FINAL LOOK section
const FinalLookSection = ({ state }) => {
  if (state === "empty") {
    return (
      <Section icon={Camera} title="Final look" visibility="private" action={<button className="text-[10px] text-purple-700 font-semibold">Upload</button>}>
        <div className="rounded-xl border-2 border-dashed border-stone-200 py-6 text-center" data-testid="final-look-empty">
          <Camera className="w-7 h-7 text-stone-300 mx-auto" />
          <p className="text-[11px] text-stone-500 mt-2">No photos yet. Your host can drop these post-service.</p>
          <p className="text-[10px] text-stone-400 mt-1">Up to 5 - you&apos;ll choose which go public.</p>
        </div>
      </Section>
    );
  }
  if (state === "single") {
    return (
      <Section icon={Camera} title="Final look (1 of 5)" visibility="public" action={<button className="text-[10px] text-purple-700 font-semibold">+ Add more</button>}>
        <FinalLookPhoto src="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600" host="Shelsea P." time="Jun 15 - 12:18 PM" isPublic />
      </Section>
    );
  }
  // multi state
  return (
    <Section icon={Camera} title="Final look (3 of 5)" visibility="public" action={<button className="text-[10px] text-purple-700 font-semibold">+ Add more</button>}>
      <div className="space-y-3">
        <FinalLookPhoto src="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600" host="Shelsea P." time="Jun 15 - 12:18 PM" isPublic isFavorite />
        <FinalLookPhoto src="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=600" host="Shelsea P." time="Jun 15 - 12:25 PM" isPublic />
        <FinalLookPhoto src="https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=600" host="Aria (selfie)" time="Jun 15 - 6:02 PM" isPublic={false} />
        <button className="w-full py-2 text-[10px] text-stone-500 border border-dashed border-stone-300 rounded-lg">2 slots remaining (5 max)</button>
      </div>
    </Section>
  );
};

const FinalLookPhoto = ({ src, host, time, isPublic, isFavorite }) => (
  <div className="rounded-xl border border-stone-200 overflow-hidden bg-white" data-testid="final-look-photo">
    <div className="aspect-square bg-cover bg-center relative" style={{ backgroundImage: `url(${src})` }}>
      {isFavorite && (
        <span className="absolute top-2 left-2 text-[8px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1">
          <Star className="w-2.5 h-2.5" fill="white" /> Top pick
        </span>
      )}
      <span className="absolute top-2 right-2 text-[8px] uppercase tracking-wider bg-white/95 text-stone-700 px-1.5 py-0.5 rounded-full font-bold">By {host}</span>
    </div>
    <div className="p-2.5 flex items-center justify-between">
      <div>
        <p className="text-[10px] text-stone-500">{time}</p>
      </div>
      <label className="flex items-center gap-1.5 cursor-pointer">
        <span className="text-[10px] text-stone-700 font-medium">Public</span>
        <div className={`w-7 h-4 rounded-full ${isPublic ? "bg-emerald-600 justify-end" : "bg-stone-300 justify-start"} flex items-center px-0.5`}>
          <div className="w-3 h-3 rounded-full bg-white" />
        </div>
      </label>
    </div>
  </div>
);

// --- Owner image picker (the 3-on-card selector)
const PCImagePicker = () => {
  const allImages = [
    { id: "i1", type: "inspo", folder: "Bridal - Inspo", url: "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300", selected: true, pos: 0 },
    { id: "i2", type: "inspo", folder: "Bridal - Inspo", url: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300", selected: false },
    { id: "i3", type: "inspo", folder: "Bridal - Inspo", url: "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300", selected: false },
    { id: "b1", type: "before", folder: "Experiences - Before", url: "https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300", selected: false },
    { id: "f1", type: "final", folder: "Experiences - Final", url: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300", selected: true, pos: 1, host: "Shelsea P." },
    { id: "f2", type: "final", folder: "Experiences - Final", url: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300", selected: true, pos: 2, host: "Shelsea P." },
    { id: "f3", type: "final", folder: "Experiences - Final", url: "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300", selected: false, host: "Aria" },
  ];
  return (
    <div className="space-y-4" data-testid="state-pc-image-picker">
      <Link to="/design/preference-card?s=after-multiple" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to card</Link>
      <div>
        <h3 className="text-lg font-bold text-stone-900">Pick 3 for card top</h3>
        <p className="text-[11px] text-stone-500">These show on your public preference card. Drag-reorder or tap to swap. (3 of 7 selected)</p>
      </div>
      {/* Current 3 preview */}
      <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3">
        <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-2">Currently showing on card</p>
        <div className="grid grid-cols-3 gap-1.5">
          {allImages.filter(i => i.selected).sort((a,b) => a.pos - b.pos).map(img =>
            <div key={img.id} className="relative aspect-square rounded-md overflow-hidden bg-cover" style={{backgroundImage:`url(${img.url})`}}>
              <span className="absolute top-1 left-1 text-[8px] uppercase tracking-wider bg-stone-900 text-white px-1 py-0.5 rounded font-bold">{img.pos + 1}</span>
              <span className="absolute bottom-1 left-1 text-[8px] uppercase tracking-wider bg-white/95 text-stone-700 px-1 py-0.5 rounded">{img.type}</span>
            </div>
          )}
        </div>
      </div>
      {/* Filter chips */}
      <div className="flex gap-1.5 text-[10px] overflow-x-auto">
        {["All 7","Inspo 3","Before 1","Final 3","Phygital shared 5"].map((t,i) =>
          <button key={t} className={`px-2.5 py-1 rounded-full whitespace-nowrap ${i === 0 ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{t}</button>
        )}
      </div>
      {/* Library grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {allImages.map(img => (
          <div key={img.id} className={`relative aspect-square rounded-md overflow-hidden border-2 ${img.selected ? "border-stone-900 ring-2 ring-purple-300" : "border-transparent"}`} data-testid={`pc-pick-image-${img.id}`}>
            <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage:`url(${img.url})`}} />
            {img.selected && (
              <span className="absolute top-1 right-1 text-[8px] uppercase tracking-wider bg-stone-900 text-white px-1 py-0.5 rounded font-bold">#{img.pos + 1}</span>
            )}
            <span className="absolute bottom-1 left-1 text-[7px] uppercase tracking-wider bg-white/95 text-stone-700 px-1 py-0.5 rounded font-bold">{img.type}</span>
            {img.host && <span className="absolute top-1 left-1 text-[7px] bg-emerald-500 text-white px-1 rounded font-bold">By {img.host.split(" ")[0]}</span>}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-stone-500 italic">Tip: hosts can upload final-look photos, but only you choose what&apos;s on the public card.</p>
      <div className="flex gap-2 pt-2">
        <Link to="/design/preference-card?s=after-multiple" className="flex-1 py-2.5 rounded-full border border-stone-200 text-xs font-medium text-center">Cancel</Link>
        <Link to="/design/preference-card?s=after-multiple" className="flex-1 py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold text-center">Save selection</Link>
      </div>
    </div>
  );
};

// --- Before image picker
const PCBeforePicker = () => (
  <div className="space-y-4" data-testid="state-pc-before-picker">
    <Link to="/design/preference-card?s=after-multiple" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Pick your before image</h3>
      <p className="text-[11px] text-stone-500">One image from your Experiences - Before folder.</p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {[
        "https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300",
        "https://images.unsplash.com/photo-1546961342-1ad994c41a3a?w=300",
        "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300",
      ].map((u, i) => (
        <div key={i} className={`relative aspect-square rounded-xl overflow-hidden border-2 ${i === 0 ? "border-stone-900 ring-2 ring-purple-300" : "border-transparent"}`}>
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage:`url(${u})`}} />
          {i === 0 && <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-stone-900 text-white px-1.5 py-0.5 rounded font-bold">Selected</span>}
          <span className="absolute bottom-2 left-2 text-[8px] uppercase tracking-wider bg-white/95 text-stone-700 px-1.5 py-0.5 rounded">Before</span>
        </div>
      ))}
      <button className="aspect-square rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-500">
        <Upload className="w-5 h-5" />
        <span className="text-[10px] mt-1 font-semibold">Upload new</span>
      </button>
    </div>
    <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3">
      <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Linked to final look</p>
      <p className="text-[11px] text-stone-700 mt-1">Before+After pairs auto-link so the experience tells a complete story.</p>
    </div>
  </div>
);

// --- Name display
const PCNameDisplay = () => (
  <div className="space-y-4" data-testid="state-pc-name-display">
    <Link to="/design/preference-card?s=no-service-done" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">How your name shows</h3>
      <p className="text-[11px] text-stone-500">Per preference card. Set defaults in Settings.</p>
    </div>
    <div className="space-y-2">
      {[
        { v: "first", title: "First name only", subtitle: "\"Aria\" - most relaxed", selected: true },
        { v: "full", title: "Full name", subtitle: "\"Aria James\" - for formal bookings" },
        { v: "initials", title: "Initials", subtitle: "\"A.J.\" - keep it private" },
        { v: "anon", title: "Anonymous", subtitle: "Just the category icon" },
      ].map(o => (
        <button key={o.v} className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 ${o.selected ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`} data-testid={`name-display-${o.v}`}>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${o.selected ? "border-stone-900 bg-stone-900" : "border-stone-300"}`}>
            {o.selected && <Check className="w-2.5 h-2.5 text-white" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-stone-900">{o.title}</p>
            <p className="text-[10px] text-stone-500">{o.subtitle}</p>
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ============================================================================
// VISIBILITY CONTROLS - replaces checkboxes with per-field 3-way selector
// ============================================================================
function VisibilityDesign() {
  const fields = [
    { key: "name", title: "Name display", subtitle: "Aria", v: "public", type: "name", options: ["full","first","initials","anon"], chosen: "first" },
    { key: "date_time", title: "Date + time", subtitle: "Jun 15 - 10:30 AM", v: "host" },
    { key: "service", title: "Service + category", subtitle: "Makeup - Full Glam", v: "public" },
    { key: "preferences", title: "Preferences (18 fields)", subtitle: "Skin type, undertone, eye shape...", v: "host" },
    { key: "products", title: "Linked products (8)", subtitle: "Fenty, Charlotte Tilbury, MAC...", v: "public" },
    { key: "inspiration", title: "Inspiration (12)", subtitle: "Pinterest + IG + uploaded", v: "public" },
    { key: "before", title: "Before look", subtitle: "1 photo + caption", v: "host" },
    { key: "final", title: "Final look (3)", subtitle: "Public toggles per-photo", v: "public" },
    { key: "budget", title: "Budget", subtitle: "Service $250 - Products $180", v: "private" },
    { key: "provider", title: "Provider details", subtitle: "Shelsea P. - Privé Beauté", v: "host" },
    { key: "location", title: "Location", subtitle: "Travel - 4321 Sunset Blvd", v: "host" },
    { key: "notes", title: "Special notes", subtitle: "Parking, allergies, access", v: "host" },
  ];
  return (
    <div className={FRAME}>
      <Header title="Visibility" back="/design/preference-card" />
      <div className="px-4 pt-3 pb-32 space-y-3 bg-stone-50" data-testid="state-visibility-main">
        <div className="rounded-2xl bg-stone-900 text-white p-4">
          <p className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Per-field visibility</p>
          <p className="text-base font-bold mt-1">Set who sees what on this card.</p>
          <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
            <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-emerald-400" /><span>Public</span></div>
            <div className="flex items-center gap-1"><Users className="w-3 h-3 text-indigo-300" /><span>Host only</span></div>
            <div className="flex items-center gap-1"><EyeOff className="w-3 h-3 text-stone-300" /><span>Eyes only</span></div>
          </div>
        </div>
        {/* preset bar */}
        <div className="flex gap-1.5">
          {[
            { v: "open", label: "Open book" },
            { v: "default", label: "Default" },
            { v: "private", label: "Locked down" },
          ].map((p, i) => (
            <button key={p.v} className={`flex-1 text-[10px] py-2 rounded-lg border ${i === 1 ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}>{p.label}</button>
          ))}
        </div>
        {/* fields */}
        <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
          {fields.map((f) => (
            <div key={f.key} className="px-3 py-3" data-testid={`vis-field-${f.key}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-stone-900 leading-tight">{f.title}</p>
                  <p className="text-[10px] text-stone-500 truncate">{f.subtitle}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1 bg-stone-100 p-0.5 rounded-lg">
                {["public","host","private"].map((v) => {
                  const opt = VIS_OPTIONS[v];
                  const Icon = opt.icon;
                  const isOn = f.v === v;
                  return (
                    <button key={v} className={`flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-md font-semibold transition ${isOn ? `bg-white shadow text-${opt.color}-700` : "text-stone-500"}`}>
                      <Icon className="w-2.5 h-2.5" />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              {f.type === "name" && (
                <div className="flex gap-1 mt-2">
                  {f.options.map((o) => (
                    <button key={o} className={`text-[9px] px-2 py-1 rounded-full ${o === f.chosen ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                      {o === "full" ? "Full name" : o === "first" ? "First only" : o === "initials" ? "Initials" : "Anonymous"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-purple-50 border border-purple-100 p-3 text-[11px] text-stone-700">
          <Sparkles className="w-3.5 h-3.5 text-purple-600 inline mr-1" />
          You can override these per-card. Set your <strong>defaults in Settings -&gt; Privacy</strong>.
        </div>
        <button className="w-full py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold">Save visibility</button>
      </div>
      <StateSwitcher flow="visibility" states={[{id:"main", label:"Per-field selector"}]} current="main" />
    </div>
  );
}

// ============================================================================
// CONFIRMATION FINAL LOOK - 3 states
// ============================================================================
const CONF_STATES = [
  { id: "empty", label: "No final look yet" },
  { id: "single", label: "1 final look" },
  { id: "multi", label: "3 final looks" },
];
function ConfirmationFinalLookDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "empty";
  return (
    <div className={FRAME}>
      <Header title="Confirmation" back="/design" />
      <div className="px-4 pt-3 pb-32 space-y-3 bg-stone-50">
        {/* abbreviated event pass */}
        <div className="rounded-2xl bg-stone-900 text-white p-4">
          <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold">Event pass</p>
          <p className="text-lg font-bold mt-1">Sister&apos;s wedding glam</p>
          <p className="text-[11px] opacity-80">Privé Beauté - Jun 15 at 10:30 AM</p>
        </div>
        {/* before look */}
        <BeforeLookSection state={s === "empty" ? "prompt" : "filled-1"} />
        {/* final look */}
        <FinalLookSection state={s} />
        <p className="text-[10px] text-stone-500 italic px-1">All photos default to <strong>host-only</strong> until you flip them public. Tap any photo to share to your Phygital share-card.</p>
      </div>
      <StateSwitcher flow="final-look" states={CONF_STATES} current={s} />
    </div>
  );
}

// ============================================================================
// MEDIA LIBRARY - sort by type, linked before/after pairs, Phygital folders
// ============================================================================
const ML_STATES = [
  { id: "sort-by-type", label: "Sort by image type" },
  { id: "before-after-pairs", label: "Before/After pairs" },
  { id: "phygital-experiences", label: "Phygital Experiences folder" },
];
function MediaLibraryDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "sort-by-type";
  return (
    <div className={FRAME}>
      <Header title="Media library" back="/design/inspo" />
      <div className="px-4 pt-3 pb-32 space-y-3 bg-stone-50">
        {/* Sort + Folder controls */}
        <div className="flex gap-1.5 overflow-x-auto">
          {[
            { id: "sort-by-type", label: "By type" },
            { id: "before-after-pairs", label: "Before/After pairs" },
            { id: "phygital-experiences", label: "Phygital Experiences" },
          ].map((t) => (
            <Link key={t.id} to={`/design/media-library?s=${t.id}`} className={`text-[11px] px-3 py-1.5 rounded-full whitespace-nowrap ${s === t.id ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700"}`}>
              {t.label}
            </Link>
          ))}
        </div>
        {s === "sort-by-type" && <MLSortByType />}
        {s === "before-after-pairs" && <MLBeforeAfterPairs />}
        {s === "phygital-experiences" && <MLPhygitalExperiences />}
      </div>
      <StateSwitcher flow="media-library" states={ML_STATES} current={s} />
    </div>
  );
}
const SectionTitle = ({ text, count }) => (
  <div className="flex items-center justify-between pt-2 px-1">
    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{text}</p>
    <span className="text-[10px] text-stone-400">{count}</span>
  </div>
);

const MLSortByType = () => (
  <div className="space-y-3" data-testid="state-ml-sort-by-type">
    <SectionTitle text="Inspo (12)" count="3 folders" />
    <div className="grid grid-cols-3 gap-1.5">
      {Array.from({length:6}).map((_,i) =>
        <div key={i} className="aspect-square rounded-md bg-cover" style={{backgroundImage:`url(https://images.unsplash.com/photo-${["1599733589046-77fde6f1f5b6","1487412947147-5cebf100ffc2","1503236823255-94609f598e71","1620916566398-39f1143ab7be","1583241800698-9c2e89f1d2f4","1512496015851-a90fb38ba796"][i]}?w=300)`}} />
      )}
    </div>
    <SectionTitle text="Before (3)" count="Linked to 3 experiences" />
    <div className="grid grid-cols-3 gap-1.5">
      {[
        "https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300",
        "https://images.unsplash.com/photo-1546961342-1ad994c41a3a?w=300",
        "https://images.unsplash.com/photo-1505951163518-ea29eb3aac28?w=300",
      ].map((u,i) =>
        <div key={i} className="relative aspect-square rounded-md bg-cover" style={{backgroundImage:`url(${u})`}}>
          <span className="absolute top-1 left-1 text-[8px] uppercase tracking-wider bg-amber-500 text-white px-1 py-0.5 rounded font-bold">Before</span>
        </div>
      )}
    </div>
    <SectionTitle text="Final look (5)" count="Up to 5 per experience" />
    <div className="grid grid-cols-3 gap-1.5">
      {[
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300",
        "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300",
        "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300",
        "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=300",
        "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300",
      ].map((u,i) =>
        <div key={i} className="relative aspect-square rounded-md bg-cover" style={{backgroundImage:`url(${u})`}}>
          <span className="absolute top-1 left-1 text-[8px] uppercase tracking-wider bg-emerald-600 text-white px-1 py-0.5 rounded font-bold">Final</span>
          <span className="absolute top-1 right-1 text-[7px] bg-white/95 text-stone-700 px-1 rounded">{i % 2 === 0 ? "By host" : "By you"}</span>
        </div>
      )}
    </div>
  </div>
);

const MLBeforeAfterPairs = () => (
  <div className="space-y-3" data-testid="state-ml-pairs">
    <p className="text-[11px] text-stone-500 px-1">Before/After auto-linked by experience. Tap a pair to compare.</p>
    {[
      { exp: "Sister&apos;s wedding glam", date: "Jun 15", before: "https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300", after: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300" },
      { exp: "Birthday lashes", date: "May 22", before: "https://images.unsplash.com/photo-1546961342-1ad994c41a3a?w=300", after: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=300" },
      { exp: "Hair color refresh", date: "Apr 18", before: "https://images.unsplash.com/photo-1505951163518-ea29eb3aac28?w=300", after: "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300" },
    ].map((p, i) => (
      <div key={i} className="rounded-2xl bg-white border border-stone-200 overflow-hidden" data-testid={`ml-pair-${i}`}>
        <div className="grid grid-cols-2 gap-px bg-stone-100">
          <div className="aspect-square bg-cover bg-center relative" style={{backgroundImage:`url(${p.before})`}}>
            <span className="absolute top-2 left-2 text-[8px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">Before</span>
          </div>
          <div className="aspect-square bg-cover bg-center relative" style={{backgroundImage:`url(${p.after})`}}>
            <span className="absolute top-2 left-2 text-[8px] uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold">After</span>
          </div>
        </div>
        <div className="p-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-stone-900">{p.exp}</p>
            <p className="text-[10px] text-stone-500 flex items-center gap-1"><Link2 className="w-2.5 h-2.5" /> linked - {p.date}</p>
          </div>
          <button className="text-[10px] text-purple-700 font-semibold">Open</button>
        </div>
      </div>
    ))}
  </div>
);

const MLPhygitalExperiences = () => (
  <div className="space-y-3" data-testid="state-ml-phygital-experiences">
    <div className="rounded-2xl bg-purple-50 border border-purple-200 p-3.5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-purple-200 flex items-center justify-center"><Sparkles className="w-5 h-5 text-purple-700" /></div>
        <div className="flex-1">
          <p className="text-sm font-bold text-stone-900">Experiences (Phygital shared)</p>
          <p className="text-[11px] text-purple-700">Hosts + clients drop final looks here. 18 photos - 5 experiences.</p>
        </div>
      </div>
    </div>
    {[
      { name: "Bridal looks", count: 6, type: "shared" },
      { name: "Birthday lashes", count: 4, type: "shared" },
      { name: "Hair refresh", count: 5, type: "shared" },
      { name: "Casual everyday", count: 3, type: "shared" },
    ].map((f) => (
      <div key={f.name} className="rounded-2xl bg-white border border-stone-200 p-3 flex items-center gap-3" data-testid={`ml-phygital-folder-${f.name.toLowerCase().replace(/ /g,'-')}`}>
        <div className="grid grid-cols-3 gap-px aspect-square w-16 rounded-md overflow-hidden flex-shrink-0">
          {[0,1,2,3,4,5,6,7,8].map(i => <div key={i} className="bg-stone-200" />)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-stone-900">{f.name}</p>
          <p className="text-[10px] text-stone-500">{f.count} photos - hosts + you</p>
        </div>
        <ChevronRight className="w-4 h-4 text-stone-400" />
      </div>
    ))}
    <button className="w-full py-2.5 rounded-full border border-dashed border-stone-300 text-stone-600 text-xs font-semibold flex items-center justify-center gap-1.5">
      <Plus className="w-3 h-3" /> Create new shared sub-folder
    </button>
  </div>
);

// ============================================================================
export default function PreferenceCardDesigns({ flow }) {
  if (flow === "preference-card") return <PreferenceCardDesign />;
  if (flow === "final-look") return <ConfirmationFinalLookDesign />;
  if (flow === "visibility") return <VisibilityDesign />;
  if (flow === "media-library") return <MediaLibraryDesign />;
  return (
    <div className={FRAME + " p-6"}>
      <h1 className="text-lg font-bold mb-3">R18 designs</h1>
      <ul className="space-y-2">
        {["preference-card","final-look","visibility","media-library"].map(f =>
          <li key={f}><Link to={`/design/${f}`} className="text-purple-700 underline text-xs">/design/{f}</Link></li>
        )}
      </ul>
    </div>
  );
}
